import React from 'react';
import { useNavigate } from 'react-router-dom';
import PlatformLayout from './PlatformLayout';

import PlatformText from './PlatformText';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <PlatformLayout>
      <div className="privacy-policy-container">
        <div className="privacy-policy-header">
          <button 
            onClick={handleBack}
            className="back-button"
          >
            ← Back
          </button>
          <PlatformText variant="h1" className="privacy-policy-title">
            Privacy Policy
          </PlatformText>
          <PlatformText variant="body" className="privacy-policy-subtitle">
            Ordease - Order Management System
          </PlatformText>
        </div>

        <div className="privacy-policy-content">
          <section className="policy-section">
            <PlatformText variant="h2">1. Introduction</PlatformText>
            <PlatformText variant="body">
              This Privacy Policy describes how Ordease ("we," "our," or "us") collects, uses, and shares your personal information when you use our order management application and related services (collectively, the "Service").
            </PlatformText>
            <PlatformText variant="body">
              By using our Service, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our Service.
            </PlatformText>
          </section>

          <section className="policy-section">
            <PlatformText variant="h2">2. Information We Collect</PlatformText>
            
            <PlatformText variant="h3">2.1 Personal Information</PlatformText>
            <PlatformText variant="body">We collect the following personal information:</PlatformText>
            <ul className="policy-list">
              <li><strong>Account Information:</strong> Email address, full name, phone number, and role within your organization</li>
              <li><strong>Organization Information:</strong> Organization name, business email, phone number, and organization code</li>
              <li><strong>Order Data:</strong> Customer names, order details, party information, and order status</li>
              <li><strong>Usage Data:</strong> How you interact with our app, features used, and performance data</li>
              <li><strong>Trial and Subscription Data:</strong> Trial status, subscription plan details, billing information, and payment history</li>
              <li><strong>Analytics Data:</strong> Dashboard metrics, order analytics, revenue data, and business performance metrics</li>
              <li><strong>Language Preferences:</strong> Your selected language (English/Hindi) and localization settings</li>
              <li><strong>Terms Acceptance:</strong> Timestamps of when you accepted our Terms of Service and Privacy Policy (termsAcceptedAt, privacyAcceptedAt)</li>
            </ul>

            <PlatformText variant="h3">2.2 Device Information</PlatformText>
            <PlatformText variant="body">We may collect information about your device, including:</PlatformText>
            <ul className="policy-list">
              <li>Device type and operating system (iOS, Android, Web, PWA)</li>
              <li>App version and build number</li>
              <li>Device identifiers and unique device IDs (UUID-based identifiers for analytics and security)</li>
              <li>Network information and connection type</li>
              <li>Platform-specific information (iOS/Android native app data)</li>
              <li>Push notification tokens for device-specific messaging</li>
            </ul>

            <PlatformText variant="h3">2.3 Permissions and Features</PlatformText>
            <PlatformText variant="body">Our app may request access to:</PlatformText>
            <ul className="policy-list">
              <li><strong>Camera:</strong> To take photos for orders, profiles, and document scanning</li>
              <li><strong>Photo Library:</strong> To upload images for orders, profiles, and document processing</li>
              <li><strong>Notifications:</strong> To send order updates, trial reminders, and important messages</li>
              <li><strong>File System:</strong> To store and manage uploaded documents and files</li>
              <li><strong>Network Access:</strong> To sync data across devices and platforms</li>
            </ul>
            <PlatformText variant="body">
              Files and documents uploaded to orders are stored with retention limits based on your subscription plan. Files may be automatically deleted after the retention period expires. Enterprise plan users have unlimited file storage.
            </PlatformText>

            <PlatformText variant="h3">2.4 Subscription and Payment Information</PlatformText>
            <PlatformText variant="body">For paid subscriptions, we collect:</PlatformText>
            <ul className="policy-list">
              <li>Payment method information (processed securely by third-party payment processors)</li>
              <li>Billing address and contact information</li>
              <li>Subscription plan details and usage limits</li>
              <li>Payment history and transaction records</li>
              <li>Trial conversion and subscription management data</li>
            </ul>
          </section>

          <section className="policy-section">
            <PlatformText variant="h2">3. How We Use Your Information</PlatformText>
            <PlatformText variant="body">We use the collected information for the following purposes:</PlatformText>
            <ul className="policy-list">
              <li>To provide and maintain our order management service across all platforms (Web, iOS, Android, PWA)</li>
              <li>To process and manage orders within your organization with real-time updates</li>
              <li>To send notifications about order updates, trial reminders, and important messages</li>
              <li>To provide customer support and respond to inquiries in your preferred language</li>
              <li>To improve our app functionality and user experience across all platforms</li>
              <li>To ensure security and prevent fraud with role-based access control</li>
              <li>To comply with legal obligations and regulatory requirements</li>
              <li>To manage your free trial and subscription services</li>
              <li>To process payments and manage billing for subscription services</li>
              <li>To provide analytics and reporting features for business insights</li>
              <li>To support multi-language functionality and localization</li>
              <li>To enable document processing and file management features</li>
              <li>To provide platform-specific features and optimizations</li>
              <li>To track usage patterns and improve service delivery</li>
              <li>To report errors and crashes for service improvement (Crashlytics)</li>
            </ul>
          </section>

          <section className="policy-section">
            <PlatformText variant="h2">4. Information Sharing and Disclosure</PlatformText>
            <PlatformText variant="body">We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:</PlatformText>
            <ul className="policy-list">
              <li><strong>Within Your Organization:</strong> Order information is shared with other members of your organization based on their role and permissions</li>
              <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our service, including:
                <ul>
                  <li>Firebase (Google) for data storage, authentication, and real-time synchronization</li>
                  <li>Google Cloud for document processing and AI services</li>
                  <li>Razorpay for payment processing and subscription billing (web-only)</li>
                  <li>Push notification services for mobile and web notifications</li>
                  <li>Analytics providers for usage tracking and service improvement</li>
                  <li>Crashlytics (Firebase) for error reporting and crash analytics</li>
                </ul>
              </li>
              <li><strong>Platform Services:</strong> We may share device-specific information with platform providers (Apple App Store, Google Play Store) for app functionality and updates</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights and safety</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred</li>
              <li><strong>Emergency Situations:</strong> We may share information to protect the safety of users or the public</li>
            </ul>
          </section>

          <section className="policy-section">
            <PlatformText variant="h2">5. Data Security</PlatformText>
            <PlatformText variant="body">We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:</PlatformText>
            <ul className="policy-list">
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication</li>
              <li>Secure data centers and infrastructure</li>
            </ul>
          </section>

          <section className="policy-section">
            <PlatformText variant="h2">6. Data Retention</PlatformText>
            <PlatformText variant="body">We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this Privacy Policy. Specifically:</PlatformText>
            <ul className="policy-list">
              <li>Account information is retained while your account is active and for 30 days after account closure</li>
              <li>Order data and uploaded files are retained based on your subscription plan's data retention period:
                <ul>
                  <li><strong>Basic Plan:</strong> 30 days - Files and order data may be automatically deleted after this period</li>
                  <li><strong>Premium Plan:</strong> 65 days - Files and order data may be automatically deleted after this period</li>
                  <li><strong>Premium+ Plan:</strong> 90 days - Files and order data may be automatically deleted after this period</li>
                  <li><strong>Enterprise Plan:</strong> Forever - No automatic deletion of files or order data</li>
                </ul>
              </li>
              <li><strong>Important:</strong> Files uploaded to orders (bills, challans, bilty documents, product images) are subject to automatic deletion after the retention period expires. We recommend downloading important files before they reach the retention limit or upgrading to a plan with longer retention.</li>
              <li>Usage data and analytics are retained for service improvement and business insights</li>
              <li>Trial data is retained for 90 days after trial expiration for conversion analysis</li>
              <li>Subscription and payment data is retained for 7 years for tax and legal compliance</li>
              <li>Device and platform information is retained for security and service optimization</li>
              <li>Terms acceptance timestamps (termsAcceptedAt, privacyAcceptedAt) are retained for legal compliance</li>
              <li>We may retain certain information for legal compliance or dispute resolution</li>
            </ul>
          </section>

          <section className="policy-section">
            <PlatformText variant="h2">7. Free Trial and Subscription Services</PlatformText>
            <PlatformText variant="body">Ordease offers a 30-day free trial for new organizations. During the trial period:</PlatformText>
            <ul className="policy-list">
              <li>You have full access to all features of the service</li>
              <li>We collect usage data to provide analytics and improve the service</li>
              <li>We may send trial-related notifications and reminders</li>
              <li>Your data is subject to the same security and privacy protections as paid users</li>
              <li>At the end of the trial period, you may choose to subscribe to a paid plan or your access will be limited</li>
            </ul>
            
            <PlatformText variant="body">For subscription services, we offer multiple plans with different features and data retention policies:</PlatformText>
            <ul className="policy-list">
              <li><strong>Basic Plan:</strong> Up to 6 users, 80 orders, 30-day backup retention</li>
              <li><strong>Premium Plan:</strong> Up to 12 users, 200 orders, 65-day backup retention</li>
              <li><strong>Premium+ Plan:</strong> Up to 20 users, 400 orders, 90-day backup retention</li>
              <li><strong>Enterprise Plan:</strong> Unlimited users and orders, forever backup retention</li>
            </ul>
          </section>

          <section className="policy-section">
            <PlatformText variant="h2">8. Your Rights and Choices</PlatformText>
            <PlatformText variant="body">Depending on your location, you may have the following rights regarding your personal information:</PlatformText>
            <ul className="policy-list">
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Objection:</strong> Object to certain processing of your information</li>
              <li><strong>Withdrawal:</strong> Withdraw consent where processing is based on consent</li>
              <li><strong>Language Preferences:</strong> Change your language settings between English and Hindi</li>
              <li><strong>Notification Preferences:</strong> Manage your notification settings for different types of alerts</li>
            </ul>
            <PlatformText variant="body">To exercise these rights, please contact us using the information provided below.</PlatformText>
          </section>

          <section className="policy-section">
            <PlatformText variant="h2">8.1 Indian Data Protection Rights (DPDPA 2023)</PlatformText>
            <PlatformText variant="body">Under India's Digital Personal Data Protection Act, 2023, you have the following specific rights:</PlatformText>
            
            <PlatformText variant="h3">8.1.1 Right to Access</PlatformText>
            <PlatformText variant="body">You have the right to obtain confirmation about whether your personal data is being processed and access to such data.</PlatformText>
            
            <PlatformText variant="h3">8.1.2 Right to Correction</PlatformText>
            <PlatformText variant="body">You have the right to request correction of inaccurate or incomplete personal data.</PlatformText>
            
            <PlatformText variant="h3">8.1.3 Right to Erasure</PlatformText>
            <PlatformText variant="body">You have the right to request deletion of your personal data when:</PlatformText>
            <ul className="policy-list">
              <li>The data is no longer necessary for the original purpose</li>
              <li>You withdraw consent and there is no other legal basis for processing</li>
              <li>The data has been unlawfully processed</li>
              <li>The data must be erased to comply with a legal obligation</li>
            </ul>
            
            <PlatformText variant="h3">8.1.4 Right to Grievance Redressal</PlatformText>
            <PlatformText variant="body">You have the right to raise grievances regarding data processing activities. We will respond within 30 days.</PlatformText>
            
            <PlatformText variant="h3">8.1.5 Right to Nomination</PlatformText>
            <PlatformText variant="body">You have the right to nominate another individual to exercise your data protection rights in case of death or incapacity.</PlatformText>
            
            <PlatformText variant="h3">8.1.6 Right to Data Portability</PlatformText>
            <PlatformText variant="body">You have the right to receive your personal data in a structured, commonly used, and machine-readable format.</PlatformText>
            
            <PlatformText variant="h3">8.1.7 Right to Withdraw Consent</PlatformText>
            <PlatformText variant="body">You can withdraw your consent for data processing at any time. Withdrawal will not affect the lawfulness of processing based on consent before withdrawal.</PlatformText>
            
            <PlatformText variant="h3">8.1.8 Right to Role and Access Management</PlatformText>
            <PlatformText variant="body">If you are an organization administrator (Business Owner), you have the right to manage user roles and access permissions within your organization. You may assign roles, approve or revoke access, and control what data team members can access.</PlatformText>
            
            <PlatformText variant="h3">8.1.9 How to Exercise Your Rights</PlatformText>
            <PlatformText variant="body">To exercise any of these rights, please contact us at:</PlatformText>
            <ul className="policy-list">
              <li><strong>Email:</strong> help.ordease@gmail.com</li>
              <li><strong>Subject Line:</strong> "DPDPA Rights Request"</li>
              <li><strong>Response Time:</strong> Within 30 days</li>
              <li><strong>Verification:</strong> We may require identity verification</li>
            </ul>
          </section>

          <section className="policy-section">
            <PlatformText variant="h2">9. International Data Transfers</PlatformText>
            <PlatformText variant="body">Your information may be transferred to and processed in countries other than your own. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards to protect your information. Our services are hosted on Google Cloud infrastructure with data centers in multiple regions.</PlatformText>
            
            <PlatformText variant="h3">9.1 Indian Data Localization</PlatformText>
            <PlatformText variant="body">For Indian users, we prioritize data storage in Indian data centers:</PlatformText>
            <ul className="policy-list">
              <li><strong>Primary Storage:</strong> Google Cloud data centers in India</li>
              <li><strong>Backup Storage:</strong> May include other Google Cloud regions for redundancy</li>
              <li><strong>Processing:</strong> Data processing occurs primarily in Indian data centers</li>
              <li><strong>Compliance:</strong> Meets Indian data localization requirements</li>
            </ul>
            
            <PlatformText variant="h3">9.2 Cross-Border Data Transfers</PlatformText>
            <PlatformText variant="body">When data is transferred outside India, we ensure:</PlatformText>
            <ul className="policy-list">
              <li>Adequate protection measures are in place</li>
              <li>Transfers are necessary for service provision</li>
              <li>Appropriate safeguards are implemented</li>
              <li>Compliance with Indian data protection laws</li>
            </ul>
          </section>

          <section className="policy-section">
            <PlatformText variant="h2">10. Indian Data Processing Under DPDPA 2023</PlatformText>
            
            <PlatformText variant="h3">10.1 Legal Basis for Processing</PlatformText>
            <PlatformText variant="body">We process personal data under the Digital Personal Data Protection Act, 2023 based on:</PlatformText>
            <ul className="policy-list">
              <li><strong>Consent:</strong> When you provide explicit consent for specific processing activities</li>
              <li><strong>Contractual Necessity:</strong> To provide our order management services</li>
              <li><strong>Legitimate Interests:</strong> For business operations, security, and service improvement</li>
              <li><strong>Legal Obligation:</strong> To comply with applicable laws and regulations</li>
            </ul>
            
            <PlatformText variant="h3">10.2 Data Processing Principles</PlatformText>
            <PlatformText variant="body">We adhere to the following DPDPA principles:</PlatformText>
            <ul className="policy-list">
              <li><strong>Fair and Lawful Processing:</strong> Data is processed fairly and lawfully</li>
              <li><strong>Purpose Limitation:</strong> Data is collected for specific, legitimate purposes</li>
              <li><strong>Data Minimization:</strong> We collect only necessary data</li>
              <li><strong>Accuracy:</strong> We maintain accurate and up-to-date data</li>
              <li><strong>Storage Limitation:</strong> Data is retained only as long as necessary</li>
              <li><strong>Security:</strong> Appropriate technical and organizational measures protect data</li>
            </ul>
            
            <PlatformText variant="h3">10.3 Sensitive Personal Data</PlatformText>
            <PlatformText variant="body">We may process sensitive personal data including:</PlatformText>
            <ul className="policy-list">
              <li>Financial information (payment details, billing information)</li>
              <li>Business information (order details, customer data)</li>
              <li>Authentication data (login credentials, security tokens)</li>
            </ul>
            <PlatformText variant="body">Such data is processed with additional safeguards and explicit consent where required.</PlatformText>
            
            <PlatformText variant="h3">10.4 Data Breach Notification</PlatformText>
            <PlatformText variant="body">In case of a data breach affecting Indian users:</PlatformText>
            <ul className="policy-list">
              <li>We will notify affected users within 72 hours</li>
              <li>We will report to the Data Protection Board of India if required</li>
              <li>We will take immediate steps to contain and remediate the breach</li>
              <li>We will provide regular updates on remediation efforts</li>
            </ul>
            
            <PlatformText variant="h3">10.5 Data Protection Impact Assessment</PlatformText>
            <PlatformText variant="body">We conduct regular assessments of our data processing activities to ensure compliance with DPDPA requirements and identify potential risks to data subjects.</PlatformText>
          </section>

          <section className="policy-section">
            <PlatformText variant="h2">11. Children's Privacy</PlatformText>
            <PlatformText variant="body">Our Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.</PlatformText>
            
            <PlatformText variant="h3">11.1 Indian Age Requirements</PlatformText>
            <PlatformText variant="body">For Indian users, our service is intended for individuals who are at least 18 years old or have parental consent. We may implement age verification measures as required by Indian law.</PlatformText>
          </section>

          <section className="policy-section">
            <PlatformText variant="h2">12. Changes to This Privacy Policy</PlatformText>
            <PlatformText variant="body">We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically. For significant changes, we will notify you through the app or via email.</PlatformText>
            
            <PlatformText variant="h3">12.1 Indian Compliance Updates</PlatformText>
            <PlatformText variant="body">We will update this policy to reflect changes in Indian data protection laws, including any amendments to the Digital Personal Data Protection Act, 2023.</PlatformText>
          </section>

          <section className="policy-section">
            <PlatformText variant="h2">13. Contact Us</PlatformText>
            <div className="contact-info">
              <PlatformText variant="body">If you have any questions about this Privacy Policy or our data practices, please contact us:</PlatformText>
              <ul className="policy-list">
                <li><strong>Email:</strong> help.ordease@gmail.com</li>
                <li><strong>Address:</strong> New Delhi, India</li>
                <li><strong>Phone:</strong> +91 9971162351</li>
                <li><strong>Website:</strong> {process.env.REACT_APP_WEBSITE_URL || 'https://www.ordease.in'}</li>
              </ul>
              
              <PlatformText variant="h3">13.1 Data Protection Officer (DPO)</PlatformText>
              <PlatformText variant="body">For data protection inquiries under DPDPA 2023:</PlatformText>
              <ul className="policy-list">
                <li><strong>DPO Email:</strong> help.ordease@gmail.com</li>
                <li><strong>Response Time:</strong> Within 30 days</li>
                <li><strong>Grievance Redressal:</strong> Available through our support channels</li>
              </ul>
            </div>
          </section>

          <section className="policy-section">
            <PlatformText variant="h2">14. California Privacy Rights (CCPA)</PlatformText>
            <PlatformText variant="body">If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information we collect and how we use it, the right to delete your personal information, and the right to opt-out of the sale of personal information. We do not sell personal information.</PlatformText>
          </section>

          <section className="policy-section">
            <PlatformText variant="h2">15. European Privacy Rights (GDPR)</PlatformText>
            <PlatformText variant="body">If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR), including the right to lodge a complaint with a supervisory authority and the right to data portability. We process your data based on legitimate business interests and contractual necessity.</PlatformText>
          </section>

          <div className="policy-footer">
            <PlatformText variant="body" className="last-updated">
              <strong>Last Updated:</strong> January 2025
            </PlatformText>
            <PlatformText variant="body" className="version">
              <strong>Version:</strong> 3.0
            </PlatformText>
          </div>
        </div>
      </div>
    </PlatformLayout>
  );
};

export default PrivacyPolicy; 